/**
 * API Client for WifiBiz Backend
 * @swagger
 * @baseUrl http://localhost:4040
 */
class ApiClient {
  constructor(baseUrl = 'http://localhost:4040', token = null, apiKey = null) {
    // Ensure baseUrl is never null or undefined
    this.baseUrl = baseUrl || 'http://localhost:4040';
    this.token = token; // JWT Bearer token (higher priority)
    this.apiKey = apiKey; // For third-party API key authentication (fallback)
  }

  /**
   * Get authentication headers
   * Token has higher priority than API key
   * @returns {Object} Headers object
   */
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Token has higher priority than API key
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else if (this.apiKey) {
      // Fallback to API key if no token
      headers['X-API-KEY'] = this.apiKey;
    }

    return headers;
  }

  /**
   * Generate random task code for Facebook Chatbot
   * @swagger
   * /task-codes/random-code:
   *   post:
   *     summary: Generate random task code
   *     description: Generates a random task code that can be used in Facebook Chatbot flows
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             type: object
   *             required:
   *               - taskId
   *               - lang
   *               - provider
   *               - user
   *             properties:
   *               taskId:
   *                 type: string
   *                 description: "Task ID"
   *                 example: "507f1f77bcf86cd799439011"
   *               lang:
   *                 type: string
   *                 enum: [vi, en]
   *                 description: "Language code"
   *                 example: "vi"
   *               provider:
   *                 type: string
   *                 enum: [smax, fchat]
   *                 description: "Chatbot provider"
   *                 example: "smax"
   *               user:
   *                 type: object
   *                 description: "User information from chatbot provider"
   *                 properties:
   *                   id:
   *                     type: string
   *                   messenger_user_id:
   *                     type: string
   *                   firstName:
   *                     type: string
   *                   lastName:
   *                     type: string
   *                   email:
   *                     type: string
   *                   phone:
   *                     type: string
   *                   gender:
   *                     type: string
   *                   locale:
   *                     type: string
   *     responses:
   *       200:
   *         description: Task code generated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 code:
   *                   type: string
   *                   description: Generated task code
   *                 success:
   *                   type: boolean
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   * @param {Object} params - Request parameters
   * @param {string} params.taskId - Task ID
   * @param {string} params.lang - Language code ('vi' or 'en')
   * @param {string} params.provider - Provider ('smax' or 'fchat')
   * @param {Object} params.user - User information object
   * @returns {Promise<Object>} API response
   */
  async generateTaskCode(params) {
    const response = await fetch(`${this.baseUrl}/task-codes/random-code`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get pages list
   * @swagger
   * /pages:
   *   get:
   *     summary: Get all pages
   *     description: Retrieve a list of all pages for the authenticated user
   *     parameters:
   *       - name: cache
   *         in: query
   *         schema:
   *           type: string
   *           enum: [yes, no]
   *         description: Cache option
   *     responses:
   *       200:
   *         description: Pages retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 rows:
   *                   type: array
   *                   items:
   *                     type: object
   *                     properties:
   *                       eid:
   *                         type: string
   *                       name:
   *                         type: string
   *                       PageSubscription:
   *                         type: object
   *                         properties:
   *                           Plan:
   *                             type: object
   *                             properties:
   *                               id:
   *                                 type: string
   *                               name:
   *                                 type: string
   * @param {Object} options - Query options
   * @param {string} [options.cache='yes'] - Cache option
   * @returns {Promise<Object>} Pages response
   */
  async getPages(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/pages?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get specific page by ID
   * @swagger
   * /pages/{id}:
   *   get:
   *     summary: Get page by ID
   *     description: Retrieve a specific page by its ID
   *     parameters:
   *       - name: id
   *         in: path
   *         required: true
   *         schema:
   *           type: string
   *         description: Page ID
   *       - name: cache
   *         in: query
   *         schema:
   *           type: string
   *           enum: [yes, no]
   *         description: Cache option
   *     responses:
   *       200:
   *         description: Page retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 eid:
   *                   type: string
   *                 name:
   *                   type: string
   *                 PageSubscription:
   *                   type: object
   *                   properties:
   *                     Plan:
   *                       type: object
   *                       properties:
   *                         id:
   *                           type: string
   *                         name:
   *                           type: string
   *       404:
   *         description: Page not found
   * @param {string} pageId - Page ID
   * @param {Object} options - Query options
   * @param {string} [options.cache='yes'] - Cache option
   * @returns {Promise<Object>} Page object
   */
  async getPage(pageId, options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/pages/${pageId}?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get plans list
   * @swagger
   * /plans:
   *   get:
   *     summary: Get all plans
   *     description: Retrieve a list of all available plans
   *     responses:
   *       200:
   *         description: Plans retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 rows:
   *                   type: array
   *                   items:
   *                     type: object
   *                     properties:
   *                       id:
   *                         type: string
   *                       name:
   *                         type: string
   *                       price:
   *                         type: number
   *                       currency:
   *                         type: string
   *                       login_per_day:
   *                         type: number
   *                       other_metrics:
   *                         type: object
   *                         properties:
   *                           task_per_day:
   *                             type: number
   * @returns {Promise<Object>} Plans response
   */
  async getPlans() {
    const response = await fetch(`${this.baseUrl}/plans`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create order
   * @swagger
   * /orders:
   *   post:
   *     summary: Create new order
   *     description: Create a new order for page upgrade
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             type: object
   *             required:
   *               - pageId
   *               - amount
   *               - planId
   *               - month
   *             properties:
   *               pageId:
   *                 type: string
   *                 description: Page ID
   *               amount:
   *                 type: number
   *                 description: Order amount
   *               planId:
   *                 type: string
   *                 description: Plan ID
   *               month:
   *                 type: number
   *                 description: Number of months
   *               isPaymentRequest:
   *                 type: boolean
   *                 description: Is payment request
   *               discountApplied:
   *                 type: number
   *                 description: Discount percentage applied
   *               originalAmount:
   *                 type: number
   *                 description: Original amount before discount
   *     responses:
   *       200:
   *         description: Order created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 status:
   *                   type: string
   *                   example: "success"
   *                 orderId:
   *                   type: string
   *       400:
   *         description: Bad request
   * @param {Object} orderData - Order data
   * @param {string} orderData.pageId - Page ID
   * @param {number} orderData.amount - Order amount
   * @param {string} orderData.planId - Plan ID
   * @param {number} orderData.month - Number of months
   * @param {boolean} [orderData.isPaymentRequest=true] - Is payment request
   * @param {number} [orderData.discountApplied] - Discount percentage applied
   * @param {number} [orderData.originalAmount] - Original amount before discount
   * @returns {Promise<Object>} Order response
   */
  async createOrder(orderData) {
    const response = await fetch(`${this.baseUrl}/orders`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get user information
   * @swagger
   * /users/{id}:
   *   get:
   *     summary: Get user by ID
   *     description: Retrieve user information by ID
   *     parameters:
   *       - name: id
   *         in: path
   *         required: true
   *         schema:
   *           type: string
   *         description: User ID
   *     responses:
   *       200:
   *         description: User retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 id:
   *                   type: string
   *                 name:
   *                   type: string
   *                 email:
   *                   type: string
   *                 UserFinance:
   *                   type: object
   *                   properties:
   *                     balance:
   *                       type: number
   *       404:
   *         description: User not found
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User object
   */
  async getUser(userId) {
    const response = await fetch(`${this.baseUrl}/users/${userId}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * User login
   * @swagger
   * /auth/login:
   *   post:
   *     summary: User login
   *     description: Authenticate user with email/mobile and password
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             type: object
   *             required:
   *               - email_mobile
   *               - password
   *             properties:
   *               email_mobile:
   *                 type: string
   *                 description: User's email or mobile number
   *               password:
   *                 type: string
   *                 description: User's password
   *     responses:
   *       200:
   *         description: Login successful
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 token:
   *                   type: string
   *                 user:
   *                   type: object
   *       400:
   *         description: Invalid credentials
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email_mobile - Email or mobile number
   * @param {string} credentials.password - Password
   * @returns {Promise<Object>} Login response with token and user
   */
  async login(credentials) {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get custom users
   * @swagger
   * /customUsers:
   *   get:
   *     summary: Get list of custom users
   *     description: Retrieve a paginated list of custom users for a specific page
   *     parameters:
   *       - name: limit
   *         in: query
   *         schema:
   *           type: integer
   *           default: 50
   *         description: Number of users to return
   *       - name: skip
   *         in: query
   *         schema:
   *           type: integer
   *           default: 0
   *         description: Number of users to skip
   *     responses:
   *       200:
   *         description: List of custom users retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 count:
   *                   type: integer
   *                 rows:
   *                   type: array
   *                   items:
   *                     type: object
   *       401:
   *         description: Unauthorized
   * @param {Object} options - Query options
   * @param {number} [options.limit=50] - Number of users to return
   * @param {number} [options.skip=0] - Number of users to skip
   * @param {string} [options.uid] - Filter by user ID
   * @param {string} [options.campaignId] - Filter by campaign ID
   * @returns {Promise<Object>} Custom users response
   */
  async getCustomUsers(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/customUsers?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get API keys
   * @swagger
   * /api-keys:
   *   get:
   *     summary: Get list of API keys
   *     description: Retrieve a paginated list of API keys for the current page
   *     parameters:
   *       - name: limit
   *         in: query
   *         schema:
   *           type: integer
   *           minimum: 1
   *           maximum: 100
   *           default: 10
   *         description: Number of API keys to return
   *       - name: offset
   *         in: query
   *         schema:
   *           type: integer
   *           minimum: 0
   *           default: 0
   *         description: Number of API keys to skip
   *     responses:
   *       200:
   *         description: List of API keys retrieved successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                 data:
   *                   type: array
   *                   items:
   *                     type: object
   *                 total:
   *                   type: integer
   *       401:
   *         description: Unauthorized
   * @param {Object} options - Query options
   * @param {number} [options.limit=10] - Number of API keys to return
   * @param {number} [options.offset=0] - Number of API keys to skip
   * @param {string} [options.search] - Search term for key name or prefix
   * @param {boolean} [options.is_active] - Filter by active status
   * @returns {Promise<Object>} API keys response
   */
  async getApiKeys(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/api-keys?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== RBAC METHODS ====================

  /**
   * Get list of scopes
   * @swagger
   * /scopes:
   *   get:
   *     summary: Get list of scopes
   *     tags: [Scopes]
   *     security:
   *       - bearerAuth: []
   *     responses:
   *       200:
   *         description: List of scopes
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   type: array
   *                   items:
   *                     $ref: '#/components/schemas/Scope'
   *       401:
   *         description: Unauthorized
   * @returns {Promise<Object>} Scopes response
   */
  async getScopes() {
    const response = await fetch(`${this.baseUrl}/scopes`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create new scope (Super Admin only)
   * @swagger
   * /scopes:
   *   post:
   *     summary: Create new scope (Super Admin only)
   *     tags: [Scopes]
   *     security:
   *       - bearerAuth: []
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/CreateScopeRequest'
   *     responses:
   *       201:
   *         description: Scope created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/Scope'
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Super Admin required)
   * @param {Object} scopeData - Scope data
   * @param {string} scopeData.name - Scope name
   * @param {string} [scopeData.description] - Scope description
   * @returns {Promise<Object>} Created scope
   */
  async createScope(scopeData) {
    const response = await fetch(`${this.baseUrl}/scopes`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(scopeData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update scope (Super Admin only)
   * @swagger
   * /scopes/{scopeId}:
   *   put:
   *     summary: Update scope (Super Admin only)
   *     tags: [Scopes]
   *     security:
   *       - bearerAuth: []
   *     parameters:
   *       - name: scopeId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/UpdateScopeRequest'
   *     responses:
   *       200:
   *         description: Scope updated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/Scope'
   *       403:
   *         description: Forbidden (Super Admin required)
   *       404:
   *         description: Scope not found
   * @param {number} scopeId - Scope ID
   * @param {Object} scopeData - Updated scope data
   * @returns {Promise<Object>} Updated scope
   */
  async updateScope(scopeId, scopeData) {
    const response = await fetch(`${this.baseUrl}/scopes/${scopeId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(scopeData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete scope (Super Admin only)
   * @swagger
   * /scopes/{scopeId}:
   *   delete:
   *     summary: Delete scope (Super Admin only)
   *     tags: [Scopes]
   *     security:
   *       - bearerAuth: []
   *     parameters:
   *       - name: scopeId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: Scope deleted successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 message:
   *                   type: string
   *                   example: "Scope deleted successfully"
   *       403:
   *         description: Forbidden (Super Admin required)
   *       404:
   *         description: Scope not found
   * @param {number} scopeId - Scope ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteScope(scopeId) {
    const response = await fetch(`${this.baseUrl}/scopes/${scopeId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get list of role templates
   * @swagger
   * /role-templates:
   *   get:
   *     summary: Get list of role templates
   *     tags: [Role Templates]
   *     security:
   *       - bearerAuth: []
   *     responses:
   *       200:
   *         description: List of role templates
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   type: array
   *                   items:
   *                     $ref: '#/components/schemas/RoleTemplate'
   *       401:
   *         description: Unauthorized
   * @returns {Promise<Object>} Role templates response
   */
  async getRoleTemplates() {
    const response = await fetch(`${this.baseUrl}/role-templates`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create new role template (Super Admin only)
   * @swagger
   * /role-templates:
   *   post:
   *     summary: Create new role template (Super Admin only)
   *     tags: [Role Templates]
   *     security:
   *       - bearerAuth: []
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/CreateRoleTemplateRequest'
   *     responses:
   *       201:
   *         description: Role template created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/RoleTemplate'
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Super Admin required)
   * @param {Object} templateData - Role template data
   * @param {string} templateData.name - Template name
   * @param {string} [templateData.description] - Template description
   * @param {number[]} [templateData.scopes] - Array of scope IDs
   * @returns {Promise<Object>} Created role template
   */
  async createRoleTemplate(templateData) {
    const response = await fetch(`${this.baseUrl}/role-templates`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update role template (Super Admin only)
   * @swagger
   * /role-templates/{templateId}:
   *   put:
   *     summary: Update role template (Super Admin only)
   *     tags: [Role Templates]
   *     security:
   *       - bearerAuth: []
   *     parameters:
   *       - name: templateId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/UpdateRoleTemplateRequest'
   *     responses:
   *       200:
   *         description: Role template updated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/RoleTemplate'
   *       403:
   *         description: Forbidden (Super Admin required)
   *       404:
   *         description: Role template not found
   * @param {number} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated role template
   */
  async updateRoleTemplate(templateId, templateData) {
    const response = await fetch(`${this.baseUrl}/role-templates/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete role template (Super Admin only)
   * @swagger
   * /role-templates/{templateId}:
   *   delete:
   *     summary: Delete role template (Super Admin only)
   *     tags: [Role Templates]
   *     security:
   *       - bearerAuth: []
   *     parameters:
   *       - name: templateId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: Role template deleted successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 message:
   *                   type: string
   *                   example: "Role template deleted successfully"
   *       403:
   *         description: Forbidden (Super Admin required)
   *       404:
   *         description: Role template not found
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteRoleTemplate(templateId) {
    const response = await fetch(`${this.baseUrl}/role-templates/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== PAGE ROLES METHODS ====================

  /**
   * Get list of page roles
   * @swagger
   * /page-roles:
   *   get:
   *     summary: Get list of page roles
   *     tags: [Page Roles]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: page
   *         in: query
   *         schema:
   *           type: integer
   *           default: 1
   *         description: Page number
   *       - name: limit
   *         in: query
   *         schema:
   *           type: integer
   *           default: 10
   *         description: Number of items per page
   *       - name: search
   *         in: query
   *         schema:
   *           type: string
   *         description: Search by name or description
   *     responses:
   *       200:
   *         description: List of page roles
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   type: array
   *                   items:
   *                     $ref: '#/components/schemas/PageRole'
   *                 pagination:
   *                   $ref: '#/components/schemas/Pagination'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   * @param {Object} options - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=10] - Items per page
   * @param {string} [options.search] - Search term
   * @returns {Promise<Object>} Page roles response
   */
  async getPageRoles(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/page-roles?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create new page role (Page Admin only)
   * @swagger
   * /page-roles:
   *   post:
   *     summary: Create new page role (Page Admin only)
   *     tags: [Page Roles]
   *     security:
   *       - pageAuth: []
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/CreatePageRoleRequest'
   *     responses:
   *       201:
   *         description: Page role created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/PageRole'
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   * @param {Object} roleData - Page role data
   * @param {string} roleData.name - Role name
   * @param {string} [roleData.description] - Role description
   * @param {number} [roleData.templateId] - Template ID to copy from
   * @param {number[]} [roleData.scopes] - Array of scope IDs
   * @returns {Promise<Object>} Created page role
   */
  async createPageRole(roleData) {
    const response = await fetch(`${this.baseUrl}/page-roles`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(roleData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update page role (Page Admin only)
   * @swagger
   * /page-roles/{roleId}:
   *   put:
   *     summary: Update page role (Page Admin only)
   *     tags: [Page Roles]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: roleId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/UpdatePageRoleRequest'
   *     responses:
   *       200:
   *         description: Page role updated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/PageRole'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   *       404:
   *         description: Page role not found
   * @param {number} roleId - Role ID
   * @param {Object} roleData - Updated role data
   * @returns {Promise<Object>} Updated page role
   */
  async updatePageRole(roleId, roleData) {
    const response = await fetch(`${this.baseUrl}/page-roles/${roleId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(roleData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete page role (Page Admin only)
   * @swagger
   * /page-roles/{roleId}:
   *   delete:
   *     summary: Delete page role (Page Admin only)
   *     tags: [Page Roles]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: roleId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: Page role deleted successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 message:
   *                   type: string
   *                   example: "Page role deleted successfully"
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   *       404:
   *         description: Page role not found
   * @param {number} roleId - Role ID
   * @returns {Promise<Object>} Delete response
   */
  async deletePageRole(roleId) {
    const response = await fetch(`${this.baseUrl}/page-roles/${roleId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== PAGE MEMBERSHIPS METHODS ====================

  /**
   * Get list of page memberships
   * @swagger
   * /page-memberships:
   *   get:
   *     summary: Get list of page memberships
   *     tags: [Page Memberships]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: page
   *         in: query
   *         schema:
   *           type: integer
   *           default: 1
   *         description: Page number
   *       - name: limit
   *         in: query
   *         schema:
   *           type: integer
   *           default: 10
   *         description: Number of items per page
   *       - name: search
   *         in: query
   *         schema:
   *           type: string
   *         description: Search by user details
   *       - name: roleId
   *         in: query
   *         schema:
   *           type: integer
   *         description: Filter by role ID
   *     responses:
   *       200:
   *         description: List of page memberships
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   type: array
   *                   items:
   *                     $ref: '#/components/schemas/PageMembership'
   *                 pagination:
   *                   $ref: '#/components/schemas/Pagination'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   * @param {Object} options - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=10] - Items per page
   * @param {string} [options.search] - Search term
   * @param {number} [options.roleId] - Filter by role ID
   * @returns {Promise<Object>} Page memberships response
   */
  async getPageMemberships(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/page-memberships?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create new page membership (Page Admin only)
   * @swagger
   * /page-memberships:
   *   post:
   *     summary: Create new page membership (Page Admin only)
   *     tags: [Page Memberships]
   *     security:
   *       - pageAuth: []
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/CreatePageMembershipRequest'
   *     responses:
   *       201:
   *         description: Page membership created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/PageMembership'
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   * @param {Object} membershipData - Membership data
   * @param {string} membershipData.userId - User ID
   * @param {number} membershipData.roleId - Role ID
   * @returns {Promise<Object>} Created page membership
   */
  async createPageMembership(membershipData) {
    const response = await fetch(`${this.baseUrl}/page-memberships`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(membershipData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update page membership role (Page Admin only)
   * @swagger
   * /page-memberships/{membershipId}:
   *   put:
   *     summary: Update page membership role (Page Admin only)
   *     tags: [Page Memberships]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: membershipId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/UpdatePageMembershipRequest'
   *     responses:
   *       200:
   *         description: Page membership updated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/PageMembership'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   *       404:
   *         description: Page membership not found
   * @param {number} membershipId - Membership ID
   * @param {Object} membershipData - Updated membership data
   * @param {number} membershipData.roleId - New role ID
   * @returns {Promise<Object>} Updated page membership
   */
  async updatePageMembership(membershipId, membershipData) {
    const response = await fetch(`${this.baseUrl}/page-memberships/${membershipId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(membershipData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Remove page membership (Page Admin only)
   * @swagger
   * /page-memberships/{membershipId}:
   *   delete:
   *     summary: Remove page membership (Page Admin only)
   *     tags: [Page Memberships]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: membershipId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: Page membership removed successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 message:
   *                   type: string
   *                   example: "Page membership removed successfully"
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   *       404:
   *         description: Page membership not found
   * @param {number} membershipId - Membership ID
   * @returns {Promise<Object>} Delete response
   */
  async deletePageMembership(membershipId) {
    const response = await fetch(`${this.baseUrl}/page-memberships/${membershipId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== API KEYS METHODS (Additional) ====================

  /**
   * Create new API key
   * @swagger
   * /api-keys:
   *   post:
   *     summary: Create new API key
   *     tags: [API Keys]
   *     security:
   *       - pageAuth: []
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/CreateApiKeyRequest'
   *     responses:
   *       201:
   *         description: API key created successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/ApiKey'
   *       400:
   *         description: Bad request
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Insufficient permissions)
   *       409:
   *         description: Conflict (API key name already exists)
   * @param {Object} keyData - API key data
   * @param {string} keyData.name - Key name
   * @param {number} [keyData.roleId] - Role ID
   * @param {string[]} [keyData.scopes] - Array of scope strings
   * @param {boolean} [keyData.isActive=true] - Whether key is active
   * @param {string} [keyData.expiresAt] - Expiration date
   * @returns {Promise<Object>} Created API key
   */
  async createApiKey(keyData) {
    const response = await fetch(`${this.baseUrl}/api-keys`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(keyData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update API key
   * @swagger
   * /api-keys/{keyId}:
   *   put:
   *     summary: Update API key
   *     tags: [API Keys]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: keyId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     requestBody:
   *       required: true
   *       content:
   *         application/json:
   *           schema:
   *             $ref: '#/components/schemas/UpdateApiKeyRequest'
   *     responses:
   *       200:
   *         description: API key updated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/ApiKey'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Insufficient permissions)
   *       404:
   *         description: API key not found
   *       409:
   *         description: Conflict (API key name already exists)
   * @param {number} keyId - API key ID
   * @param {Object} keyData - Updated key data
   * @returns {Promise<Object>} Updated API key
   */
  async updateApiKey(keyId, keyData) {
    const response = await fetch(`${this.baseUrl}/api-keys/${keyId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(keyData)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete API key
   * @swagger
   * /api-keys/{keyId}:
   *   delete:
   *     summary: Delete API key
   *     tags: [API Keys]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: keyId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: API key deleted successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 message:
   *                   type: string
   *                   example: "API key deleted successfully"
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Insufficient permissions)
   *       404:
   *         description: API key not found
   * @param {number} keyId - API key ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteApiKey(keyId) {
    const response = await fetch(`${this.baseUrl}/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Regenerate API key
   * @swagger
   * /api-keys/{keyId}/regenerate:
   *   post:
   *     summary: Regenerate API key
   *     tags: [API Keys]
   *     security:
   *       - pageAuth: []
   *     parameters:
   *       - name: keyId
   *         in: path
   *         required: true
   *         schema:
   *           type: integer
   *         example: 1
   *     responses:
   *       200:
   *         description: API key regenerated successfully
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   $ref: '#/components/schemas/ApiKey'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Insufficient permissions)
   *       404:
   *         description: API key not found
   * @param {number} keyId - API key ID
   * @returns {Promise<Object>} Regenerated API key
   */
  async regenerateApiKey(keyId) {
    const response = await fetch(`${this.baseUrl}/api-keys/${keyId}/regenerate`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get list of users
   * @swagger
   * /users:
   *   get:
   *     summary: Get list of users
   *     tags: [Users]
   *     security:
   *       - bearerAuth: []
   *     parameters:
   *       - name: limit
   *         in: query
   *         schema:
   *           type: integer
   *           default: 10
   *         description: Number of items per page
   *       - name: page
   *         in: query
   *         schema:
   *           type: integer
   *           default: 1
   *         description: Page number
   *       - name: search
   *         in: query
   *         schema:
   *           type: string
   *         description: Search by user details
   *     responses:
   *       200:
   *         description: List of users
   *         content:
   *           application/json:
   *             schema:
   *               type: object
   *               properties:
   *                 success:
   *                   type: boolean
   *                   example: true
   *                 data:
   *                   type: array
   *                   items:
   *                     $ref: '#/components/schemas/PageMembership'
   *                 pagination:
   *                   $ref: '#/components/schemas/Pagination'
   *       401:
   *         description: Unauthorized
   *       403:
   *         description: Forbidden (Page Admin required)
   * @param {Object} options - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=10] - Items per page
   * @param {string} [options.search] - Search term
   * @returns {Promise<Object>} Users response
   */
  async getUsers(options = {}) {
    const queryParams = new URLSearchParams(options);
    const response = await fetch(`${this.baseUrl}/users?${queryParams}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}

export default ApiClient;
